import React from 'react';

// Brand colors
const CYAN_PRIMARY = 'var(--pm-cyan)';
const CYAN_DIM = 'var(--pm-cyan-glow)';
const CYAN_GLOW = 'var(--pm-cyan-glow)';
const GRAY_DIM = 'var(--pm-gray-dim)';
const GRAY_TEXT = 'var(--pm-gray-text)';
const GRAY_TRACK = 'var(--pm-gray-track)';
const BG_DARK = 'var(--pm-bg-base)';

interface AudioGenerationAnimationProps {
  completedClips: number;
  totalClips: number;
}

// Small "x" sparkle mark
const SparkleX: React.FC<{ size: number; delay: number; style?: React.CSSProperties }> = ({
  size,
  delay,
  style
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 12 12"
    style={{
      position: 'absolute',
      animation: `sparkle 3s ease-in-out infinite`,
      animationDelay: `${delay}s`,
      ...style,
    }}
  >
    <path
      d="M2 2L10 10M10 2L2 10"
      stroke={CYAN_PRIMARY}
      strokeWidth="2"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

const AudioGenerationAnimation: React.FC<AudioGenerationAnimationProps> = ({
  completedClips,
  totalClips,
}) => {
  // Calculate progress percentage
  const progress = totalClips > 0 ? (completedClips / totalClips) * 100 : 0;

  // SVG circle parameters for progress ring
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  // Script document line widths
  const scriptLines = [65, 85, 75, 90, 60];

  // Waveform bar heights (symmetrical pattern)
  const waveformBars = [0.4, 0.7, 0.9, 1, 0.9, 0.7, 0.4];

  return (
    <>
      {/* Keyframe animations */}
      <style>{`
        @keyframes linePulse {
          0%, 100% {
            opacity: 0.4;
          }
          50% {
            opacity: 0.8;
          }
        }

        @keyframes dotPulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.6;
          }
          50% {
            transform: scale(1.3);
            opacity: 1;
          }
        }

        @keyframes waveBar {
          0%, 100% {
            transform: scaleY(1);
          }
          50% {
            transform: scaleY(0.5);
          }
        }

        @keyframes sparkle {
          0%, 100% {
            opacity: 0;
            transform: scale(0.5) rotate(0deg);
          }
          50% {
            opacity: 0.8;
            transform: scale(1) rotate(90deg);
          }
        }

        @keyframes glowPulse {
          0%, 100% {
            box-shadow: 0 0 20px ${CYAN_DIM}, 0 0 40px rgba(0, 212, 255, 0.1);
          }
          50% {
            box-shadow: 0 0 30px ${CYAN_GLOW}, 0 0 60px rgba(0, 212, 255, 0.2);
          }
        }

        @keyframes flowPulse {
          0%, 100% {
            opacity: 0.5;
          }
          50% {
            opacity: 1;
          }
        }

        @keyframes progressGlow {
          0%, 100% {
            filter: drop-shadow(0 0 6px ${CYAN_DIM});
          }
          50% {
            filter: drop-shadow(0 0 12px ${CYAN_GLOW});
          }
        }
      `}</style>

      {/* Full container matching other status animations */}
      <div
        className="border border-border rounded-lg p-10 flex items-center justify-center"
        style={{ background: 'var(--pm-gradient-dark)' }}
      >
        <div className="text-center">
          <h3 className="text-h1 mb-2">Generating Audio</h3>
          <p className="text-muted-foreground mb-8">
            Converting scripts to voiceover audio...
          </p>

          {/* Animation content - vertical layout */}
          <div className="flex flex-col items-center gap-8">
            {/* Script → Flow → Waveform row */}
            <div className="flex items-center gap-6">
              {/* Script Document */}
              <div
                className="relative rounded-lg overflow-hidden"
                style={{
                  width: '90px',
                  height: '110px',
                  backgroundColor: BG_DARK,
                  border: `2px solid ${CYAN_PRIMARY}`,
                  animation: 'glowPulse 3s ease-in-out infinite',
                }}
              >
                {/* Folded corner */}
                <div
                  className="absolute bottom-0 right-0"
                  style={{
                    width: '16px',
                    height: '16px',
                    background: `linear-gradient(135deg, transparent 50%, ${CYAN_DIM} 50%)`,
                  }}
                />
                {/* Text lines */}
                <div className="p-3 space-y-2">
                  {scriptLines.map((width, index) => (
                    <div
                      key={index}
                      className="h-1 rounded-full"
                      style={{
                        width: `${width}%`,
                        backgroundColor: GRAY_DIM,
                        animation: `linePulse 2s ease-in-out infinite`,
                        animationDelay: `${index * 0.2}s`,
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Flow Indicator */}
              <div
                className="flex items-center gap-0"
                style={{ animation: 'flowPulse 2s ease-in-out infinite' }}
              >
                {/* Line segment */}
                <div
                  className="h-[2px] w-6"
                  style={{ backgroundColor: CYAN_DIM }}
                />
                {/* First dot */}
                <div
                  className="w-3 h-3 rounded-full"
                  style={{
                    backgroundColor: CYAN_PRIMARY,
                    animation: 'dotPulse 1.5s ease-in-out infinite',
                  }}
                />
                {/* Line segment */}
                <div
                  className="h-[2px] w-6"
                  style={{ backgroundColor: CYAN_DIM }}
                />
                {/* Second dot */}
                <div
                  className="w-3 h-3 rounded-full"
                  style={{
                    backgroundColor: CYAN_PRIMARY,
                    animation: 'dotPulse 1.5s ease-in-out infinite',
                    animationDelay: '0.3s',
                  }}
                />
                {/* Arrow */}
                <div
                  className="h-[2px] w-4"
                  style={{ backgroundColor: CYAN_DIM }}
                />
                <div
                  className="w-0 h-0"
                  style={{
                    borderTop: '6px solid transparent',
                    borderBottom: '6px solid transparent',
                    borderLeft: `10px solid ${CYAN_PRIMARY}`,
                  }}
                />
              </div>

              {/* Waveform */}
              <div className="relative">
                {/* Sparkles around waveform */}
                <SparkleX size={10} delay={0} style={{ top: '-12px', right: '-12px' }} />
                <SparkleX size={8} delay={1.5} style={{ bottom: '-14px', left: '50%', transform: 'translateX(-50%)' }} />

                <div
                  className="relative rounded-lg flex items-center justify-center"
                  style={{
                    width: '90px',
                    height: '110px',
                    backgroundColor: BG_DARK,
                    border: `2px solid ${CYAN_PRIMARY}`,
                    animation: 'glowPulse 3s ease-in-out infinite',
                    animationDelay: '0.5s',
                  }}
                >
                  {/* Waveform bars */}
                  <div className="flex items-center gap-1">
                    {waveformBars.map((heightRatio, index) => (
                      <div
                        key={index}
                        className="rounded-sm"
                        style={{
                          width: '6px',
                          height: `${50 * heightRatio}px`,
                          backgroundColor: CYAN_PRIMARY,
                          animation: `waveBar 0.8s ease-in-out infinite`,
                          animationDelay: `${index * 0.1}s`,
                          transformOrigin: 'center',
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Section */}
            <div className="flex flex-col items-center gap-3">
              {/* Circular Progress Ring */}
              <div
                className="relative"
                style={{
                  width: '120px',
                  height: '120px',
                  animation: 'progressGlow 2s ease-in-out infinite',
                }}
              >
                <svg width="120" height="120" className="transform -rotate-90">
                  {/* Background track */}
                  <circle
                    cx="60"
                    cy="60"
                    r={radius}
                    fill="none"
                    stroke={GRAY_TRACK}
                    strokeWidth="6"
                  />
                  {/* Progress stroke */}
                  <circle
                    cx="60"
                    cy="60"
                    r={radius}
                    fill="none"
                    stroke={CYAN_PRIMARY}
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    style={{
                      transition: 'stroke-dashoffset 0.5s ease-out',
                    }}
                  />
                </svg>

                {/* Center text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span
                    className="text-3xl font-bold"
                    style={{ color: CYAN_PRIMARY }}
                  >
                    {completedClips}
                  </span>
                  <span className="text-sm" style={{ color: GRAY_TEXT }}>
                    of {totalClips}
                  </span>
                </div>
              </div>

              {/* Label below ring */}
              <span className="text-base" style={{ color: GRAY_TEXT }}>
                audio clips ready
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AudioGenerationAnimation;
